// @ts-nocheck
import React from 'react';
import { Grid, Column, Tile, Tag, Button, Heading, Stack, Layer, Breadcrumb, BreadcrumbItem } from '@carbon/react';
import styles from './FeaturesPage.module.scss';
import {
  ArrowRight,
  ChartLineData,
  User,
  Settings,
  Security,
  Analytics,
  Mobile,
  Time,
  GroupObjects,
} from '@carbon/icons-react';
import { MarketingVariant } from '../../routes/MarketingRouter';
import MarketingHeader from '../../components/marketing/MarketingHeader';
import MarketingFooter from '../../components/marketing/MarketingFooter';
import FeaturesSection from '../../components/marketing/FeaturesSection';

interface FeaturesPageProps {
  variant?: MarketingVariant;
}

const FeaturesPage: React.FC<FeaturesPageProps> = ({ variant = 'default' }) => {
  const features = [
    {
      icon: ChartLineData,
      title: 'AI-Powered Analytics',
      description: 'Real-time insights into student progress and learning patterns with predictive analytics.',
      tags: ['AI', 'Analytics', 'Real-time'],
    },
    {
      icon: User,
      title: 'Personalized Learning',
      description: "Adaptive curriculum that adjusts to each student's learning style and pace.",
      tags: ['Personalization', 'Adaptive', 'Student-Centered'],
    },
    {
      icon: GroupObjects,
      title: 'Family Collaboration',
      description: 'Seamless communication between students, teachers, and parents.',
      tags: ['Communication', 'Family', 'Collaboration'],
    },
    {
      icon: Security,
      title: 'Enterprise Security',
      description: 'FERPA compliant, COPPA certified, and SOC 2 Type II certified security.',
      tags: ['FERPA', 'COPPA', 'SOC 2'],
    },
    {
      icon: Mobile,
      title: 'Mobile Learning',
      description: 'Full-featured mobile apps for iOS and Android with offline capabilities.',
      tags: ['Mobile', 'Offline', 'Cross-platform'],
    },
    {
      icon: Time,
      title: 'Time Savings',
      description: 'Automated grading and lesson planning save educators 5+ hours per week.',
      tags: ['Automation', 'Efficiency', 'Time Management'],
    },
  ];

  return (
    <div className={styles.featuresPage}>
      <MarketingHeader variant={variant} />

      {/* Breadcrumb Navigation */}
      <Grid narrow>
        <Column lg={16} md={8} sm={4} className={styles.breadcrumbContainer}>
          <Breadcrumb noTrailingSlash aria-label="Page navigation">
            <BreadcrumbItem href="/">Home</BreadcrumbItem>
            <BreadcrumbItem href="/features" isCurrentPage>
              Features
            </BreadcrumbItem>
          </Breadcrumb>
        </Column>
      </Grid>

      {/* Hero Section */}
      <Layer>
        <Grid narrow>
          <Column lg={16} md={8} sm={4}>
            <Stack gap={8} className={styles.heroSection}>
              <Stack gap={4}>
                <Heading className="cds--type-expressive-heading-06">Powerful Features for Modern Education</Heading>
                <p className={`cds--type-body-02 ${styles.heroDescription}`}>
                  Comprehensive features designed for {variant === 'default' ? 'everyone' : `${variant}s`}
                  in the educational ecosystem
                </p>
              </Stack>
              <Button kind="primary" size="lg" renderIcon={ArrowRight}>
                Start Free Trial
              </Button>
            </Stack>
          </Column>
        </Grid>
      </Layer>

      {/* Features Grid */}
      <Layer>
        <Grid narrow className={styles.featuresGrid}>
          {features.map((feature, index) => (
            <Column key={index} lg={5} md={4} sm={4}>
              <Tile className={styles.featureCard}>
                <Stack gap={4}>
                  <feature.icon size={32} className="cds--icon" />
                  <Heading className="cds--type-productive-heading-03">{feature.title}</Heading>
                  <p className="cds--type-body-01">{feature.description}</p>
                  <Stack gap={2} orientation="horizontal" wrap>
                    {feature.tags.map((tag, tagIndex) => (
                      <Tag key={tagIndex} type="outline" size="sm">
                        {tag}
                      </Tag>
                    ))}
                  </Stack>
                </Stack>
              </Tile>
            </Column>
          ))}
        </Grid>
      </Layer>

      {/* Include detailed features section */}
      <FeaturesSection variant={variant} onFeatureInteraction={() => {}} />

      <MarketingFooter />
    </div>
  );
};

export default FeaturesPage;
